import * as React from 'react';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';

import browserLanguage from 'Utility/browserLanguage';
import { getICD10Description, getICD10Codes } from 'Utility/randomUtil';
import { equals } from 'ramda';
import { useAppSelector as useSelector } from 'Store/index';
import type { IFormContext } from 'Containers/FormContextHandler';
import { withFormContext } from 'Containers/FormContextHandler';

const PolyneuroUnderlyingCondition = ({
  documents,
  editing,
  formData,
}: IFormContext<IPolyneuroUnderlyingCondition>): React.JSX.Element => {
  const uiLanguage = useSelector((state: IState) => state.settings.userSettings.uiLanguage) ?? browserLanguage;
  const [codeList, setCodeList] = React.useState<Array<ICD10Item>>([]);
  const [searchString, setSearchString] = React.useState<string>('');
  // @ts-ignore - TODO
  const documentsOnMount = React.useRef<Array<IDocumentWithTypeV2<'polyneuroUnderlyingCondition'>>>(documents);

  const updateICD10Codes = React.useCallback((): void => {
    const locale = uiLanguage; // 'fi' or 'se' or 'en'
    if (searchString && searchString.length > 2) {
      getICD10Codes(searchString, locale)
        .then(data => {
          setCodeList(data);
        })
        .catch(() => {
          return;
        });
    }
  }, [searchString]);

  const onChangeICD = (e: React.SyntheticEvent<Element, Event>, d: string): void => {
    d && d !== 'undefined' ? setSearchString(d) : d === '' ? setSearchString('') : undefined;
  };

  React.useEffect(() => {
    // Load ICD-10 after typing has been stopped
    const timeout = setTimeout(() => {
      updateICD10Codes();
    }, 750);
    if (equals(documents, documentsOnMount.current)) {
      return;
    }
    return () => clearTimeout(timeout);
  }, [documents, formData.document.date, updateICD10Codes]);

  React.useEffect(() => {
    const locale = uiLanguage;

    let isMounted = true;
    if (formData.document.classification?.icd10code) {
      getICD10Description(formData.document.classification.icd10code, locale)
        .then(r => {
          if (isMounted && r !== formData.document.classification?.icd10description) {
            formData.onChange?.({
              classification: {
                icd10code: formData.document.classification?.icd10code,
                icd10description: r ?? formData.document.classification?.icd10description,
              },
            });
          }
        })
        .catch(() => {
          return;
        });
    }
    return (): void => {
      isMounted = false;
    };
  }, [formData]);

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          name="date"
          editing={!!editing}
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="diagnosis.polyneuro.underlyingCondition.icd10">
        <InputHandler
          type="AutoCompleteSelect"
          name="classification"
          editing={!!editing}
          formData={formData}
          options={codeList}
          placeholder={'diagnosis.polyneuro.underlyingCondition.choose'}
          labelID={'icd10description'}
          groupID={'icd10code'}
          onInputChange={onChangeICD}
          getOptionLabel={(o: ICD10Item): string =>
            `${o.icd10code}${o.icd10description ? ' - ' + o.icd10description : ''}`
          }
          width={50}
        />
      </FormRow>
    </React.Fragment>
  );
};

export default withFormContext(PolyneuroUnderlyingCondition);
